// src/services/api.service.js
import { auth } from "../firebase/config";
import { API_BASE_URL, ERROR_MESSAGES } from "../constants";

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.tokenCache = null;
    this.tokenExpiry = null;
    this.tokenUserUid = null; 
  }

  async getAuthToken() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error(ERROR_MESSAGES.AUTH_FAILED);
    }
    
    try {
      // Solo refrescar si el token está por expirar (dentro de 5 minutos)
      const now = Date.now();
      if (
        this.tokenCache &&
        this.tokenExpiry &&
        this.tokenExpiry > now + 5 * 60 * 1000 &&
        this.tokenUserUid === user.uid
      ) {
        return this.tokenCache;
      }

      // NO forzar refresh - Firebase maneja la expiración automáticamente
      const token = await user.getIdToken(false);
      
      // Cachear el token (los tokens de Firebase duran 1 hora)
      this.tokenCache = token;
      this.tokenExpiry = now + 55 * 60 * 1000; // Renovar 5 minutos antes de expirar
      this.tokenUserUid = user.uid;
      
      return token;
    } catch (error) {
      console.error("Error getting auth token:", error);
      // Limpiar cache en caso de error
       this.clearTokenCache();
      throw new Error(ERROR_MESSAGES.SESSION_EXPIRED);
    }
  }

  clearTokenCache() {
    this.tokenCache = null;
    this.tokenExpiry = null;
  }

  async request(endpoint, options = {}) {
    const { requiresAuth = true, ...fetchOptions } = options;

    const config = {
      ...fetchOptions,
      headers: {
        "Content-Type": "application/json",
        ...fetchOptions.headers
      }
    };

    if (requiresAuth) {
      try {
        const token = await this.getAuthToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error("Auth error:", error);
        throw error;
      }
    }

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);

      if (response.status === 401) {
        // Limpiar cache y refrescar token
        this.clearTokenCache();
        
        if (requiresAuth) {
          try {
            const freshToken = await this.getAuthToken();
            config.headers.Authorization = `Bearer ${freshToken}`;
            
            // Retry la petición con el token fresco
            const retryResponse = await fetch(`${this.baseURL}${endpoint}`, config);
            
            if (retryResponse.status === 401) {
              throw new Error(ERROR_MESSAGES.SESSION_EXPIRED);
            }
            
            if (!retryResponse.ok) {
              const errorData = await retryResponse.json().catch(() => ({}));
              throw new Error(errorData.detail || ERROR_MESSAGES.GENERIC);
            }
            
            if (retryResponse.status === 204) {
              return null;
            }
            
            return await retryResponse.json();
          } catch (retryError) {
            throw new Error(ERROR_MESSAGES.SESSION_EXPIRED);
          }
        }
        throw new Error(ERROR_MESSAGES.SESSION_EXPIRED);
      }

      if (response.status === 403) {
        throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || ERROR_MESSAGES.GENERIC);
      }

      // Para DELETE que no retorna contenido
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      if (error.message.includes("fetch") || error.message.includes("Failed to fetch")) {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }
      throw error;
    }
  }

  // ============ USUARIOS ============
  async getCurrentUser() {
    return this.request("/usuarios/me");
  }

  async syncUser(uid, email, nombre = "", telefono = "") {
    const token = await this.getAuthToken();
    return fetch(`${this.baseURL}/usuarios/sync`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ uid, email, nombre, telefono })
    });
  }

  async updateLastLogin() {
    return this.request("/usuarios/login", { method: "POST" });
  }

  async getAllUsers() {
    return this.request("/usuarios/");
  }

  async updateUserRole(userId, newRole) {
    return this.request(`/usuarios/${userId}/rol`, {
      method: "PUT",
      body: JSON.stringify({ nuevo_rol: newRole })
    });
  }

  async deactivateUser(userId) {
    return this.request(`/usuarios/${userId}`, { method: "DELETE" });
  };

  async activateUser(userId) {
      return this.request(`/usuarios/${userId}`, { method: "PATCH" });
    };

  // Actualizar credenciales (email y/o contraseña)
  async updateCredentials(data) {
    return this.request("/usuarios/me/credenciales", {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }

  async updateProfile(data) {
    return this.request("/usuarios/me", {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }

  // ============ RECLAMOS ============
  async getAllReclamos() {
    return this.request("/reclamos/", { requiresAuth: false });
  }

  async getMyReclamos() {
    return this.request("/reclamos/mis-reclamos");
  }

  async createReclamo(reclamoData) {
    return this.request("/reclamos/", {
      method: "POST",
      body: JSON.stringify(reclamoData)
    });
  }

  async updateReclamoEstado(reclamoId, nuevoEstado) {
    return this.request(`/reclamos/${reclamoId}/estado`, {
      method: "PUT",
      body: JSON.stringify({ estado: nuevoEstado })
    });
  }

  async updateReclamoPrioridad(reclamoId, nuevaPrioridad) {
    return this.request(`/reclamos/${reclamoId}/prioridad`, {
      method: "PUT",
      body: JSON.stringify({ prioridad: nuevaPrioridad })
    });
  }

  async deleteReclamo(reclamoId) {
    return this.request(`/reclamos/${reclamoId}`, { method: "DELETE" });
  }

  // ============ HISTORIAL ============
  async getHistorialByReclamo(reclamoId) {
    return this.request(`/historial/reclamo/${reclamoId}`, { requiresAuth: false });
  }

  async createHistorial(data) {
    return this.request("/historial/", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  async updateHistorial(historialId, data) {
    return this.request(`/historial/${historialId}`, {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }

  async getMyHistorial() {
    return this.request("/historial/mi-historial");
  }

  // ============ FEEDBACK ============
  async getFeedbacksByReclamo(reclamoId) {
    return this.request(`/feedbacks/reclamo/${reclamoId}`, { requiresAuth: false });
  }

  async getAllFeedbacksAgrupados() {
    return this.request("/feedbacks/todos-agrupados", { requiresAuth: false });
  }

  async createFeedback(data) {
    return this.request("/feedbacks/", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  async updateFeedback(feedbackId, data) {
    return this.request(`/feedbacks/${feedbackId}`, {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }

  async deleteFeedback(feedbackId) {
    return this.request(`/feedbacks/${feedbackId}`, { method: "DELETE" });
  }

  async getMyFeedbacks() {
    return this.request("/feedbacks/mis-feedbacks");
  }

  // ============ IMÁGENES ============
  async getImagenesByReclamo(reclamoId) {
    return this.request(`/imagenes/reclamo/${reclamoId}`);
  }

  async deleteImagen(imagenId) {
    return this.request(`/imagenes/${imagenId}`, { method: "DELETE" });
  }

  async createImagen(imagenData) {
    return this.request("/imagenes", {
      method: "POST",
      body: JSON.stringify(imagenData)
    });
  }

  // ============ DASHBOARD ============
  async getDashboardStats() {
      return this.request("/dashboard/stats", {
      });
  };

async getPublicDashboard() {
  return this.request("/dashboard/public", {
    requiresAuth: false, 
  });
}


}

const apiService = new ApiService();
export default apiService;